import React, { useRef, useState } from 'react'
import BaseTable, {
  ColumnDefinition,
  RowVariant
} from 'presentation/shared/components/BaseTable'
import { ReactComponent as EditIcon } from 'presentation/assets/icons/edit-rounded.svg'
import { ReactComponent as TrashIcon } from 'presentation/assets/icons/delete-rounded.svg'
import { ReactComponent as AlertIcon } from 'presentation/assets/icons/alert.svg'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import {
  QuantityWrapper,
  QuantityInput,
  EditIconButton
} from '../components/styles'
import { OpmeV2Props } from 'presentation/contexts/surgical-order-v2/types'
import { DataSource } from 'common/enum/data-source'
import {
  DeleteIconButton,
  StyledCheckbox,
  WarningMessage,
  JustificationWrapper
} from '../../components/styles'

import { DescriptionWrapper } from './styles'

type Props = {
  initialRows: OpmeV2Props[]
  onDelete: (opmeCode: string) => void
  onQuantityChange?: (opmeCode: string, quantity: number) => void
  onToggleSelection?: (opmeCode: string) => void
  justification?: string
  onJustificationChange?: (value: string) => void
  hideSections?: boolean
}

function OpmesTable({
  initialRows,
  onDelete,
  onQuantityChange,
  onToggleSelection,
  justification = '',
  onJustificationChange,
  hideSections = false
}: Props) {
  const hospitalRows = initialRows.filter(
    (row) => row.source === DataSource.TEMPLATE
  )
  const doctorRows = initialRows.filter(
    (row) => row.source === DataSource.DOCTOR
  )

  const isFromHospitalTemplate = (opmeCode: string) => {
    return hospitalRows.some((row) => row.code === opmeCode)
  }

  const shouldShowJustification = hospitalRows.some(
    (row) => row.low_acceptance && row.is_selected
  )

  const sections = hideSections
    ? [{ title: '', rows: initialRows }]
    : [
        {
          title: 'Gabarito',
          rows: hospitalRows,
          renderAfterSection: shouldShowJustification
            ? () => (
                <JustificationWrapper>
                  <TextAreaNew
                    label="Para incluir opme que tem histórico de negativa pela operadora, necessário justificativa especial"
                    labelColor="danger700"
                    labelBgColor="danger50"
                    labelOverlapping
                    bgColor="transparent"
                    outline
                    outlineColor="danger700"
                    outlineFocusColor="danger700"
                    placeholder="Descreva o motivo da solicitação deste(s) OPME(s)"
                    value={justification}
                    onInputChange={onJustificationChange}
                    rows={3}
                  />
                </JustificationWrapper>
              )
            : undefined
        },
        {
          title: 'Inclusões do médico',
          rows: doctorRows
        }
      ]
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})
  const [editingValues, setEditingValues] = useState<{ [key: string]: string }>(
    {}
  )

  const handleEditClick = (code: string) => {
    inputRefs.current[code]?.focus()
    inputRefs.current[code]?.select()
  }

  const handleQuantityChange = (code: string, value: string) => {
    const numericValue = value.replace(/\D/g, '')
    setEditingValues((prev) => ({ ...prev, [code]: numericValue }))

    if (numericValue !== '') {
      const quantity = parseInt(numericValue, 10)
      if (!isNaN(quantity) && quantity > 0 && quantity <= 99) {
        onQuantityChange?.(code, quantity)
      }
    }
  }

  const handleQuantityBlur = (code: string, value: string) => {
    const numericValue = value.replace(/\D/g, '')
    const quantity = parseInt(numericValue, 10)

    if (isNaN(quantity) || quantity <= 0 || numericValue === '') {
      onQuantityChange?.(code, 1)
    }

    setEditingValues((prev) => {
      const newValues = { ...prev }
      delete newValues[code]
      return newValues
    })
  }

  const handleQuantityFocus = (code: string, currentQuantity: number) => {
    const formattedValue =
      currentQuantity < 10 ? `0${currentQuantity}` : currentQuantity.toString()
    setEditingValues((prev) => ({ ...prev, [code]: formattedValue }))
  }

  const columns: ColumnDefinition<OpmeV2Props>[] = [
    {
      key: 'checkbox',
      header: '',
      width: '5%',
      centered: true,
      render: function renderCheckbox(row: OpmeV2Props) {
        const isCheckboxDisabled = !isFromHospitalTemplate(row.code)

        return (
          <StyledCheckbox
            checked={row.is_selected || false}
            onChange={() => onToggleSelection?.(row.code)}
            disabled={isCheckboxDisabled}
            $lowAcceptance={row.low_acceptance}
          />
        )
      }
    },
    {
      key: 'description',
      header: 'OPME',
      width: '80%',
      render: (row) => {
        if (row.low_acceptance) {
          return (
            <DescriptionWrapper>
              <div>{row.description}</div>
              <WarningMessage>
                <AlertIcon width={16} height={16} />
                <span>Este OPME foi negado em solicitações anteriores</span>
              </WarningMessage>
            </DescriptionWrapper>
          )
        }
        return row.description
      }
    },
    {
      key: 'quantity',
      header: 'Quant.',
      width: '5%',
      centered: true,
      render: function renderQuantity(row) {
        const isEditing = row.code in editingValues
        const displayValue = isEditing
          ? editingValues[row.code]
          : row.quantity < 10
          ? `0${row.quantity}`
          : row.quantity.toString()

        return (
          <QuantityWrapper>
            <QuantityInput
              ref={(el) => (inputRefs.current[row.code] = el)}
              type="text"
              value={displayValue}
              maxLength={2}
              onChange={(e) => handleQuantityChange(row.code, e.target.value)}
              onBlur={(e) => handleQuantityBlur(row.code, e.target.value)}
              onFocus={() => handleQuantityFocus(row.code, row.quantity)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
            />
            <EditIconButton
              size="small"
              onClick={() => handleEditClick(row.code)}
            >
              <EditIcon width={20} height={20} />
            </EditIconButton>
          </QuantityWrapper>
        )
      }
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '10%',
      centered: true,
      render: function renderActions(row) {
        const isDisabled = isFromHospitalTemplate(row.code)

        return (
          <DeleteIconButton
            size="small"
            onClick={() => onDelete(row.code)}
            disabled={isDisabled}
          >
            <TrashIcon width={20} height={20} />
          </DeleteIconButton>
        )
      }
    }
  ]

  const getRowVariant = (row: OpmeV2Props): RowVariant => {
    if (row.low_acceptance) {
      return 'danger'
    }
    return 'default'
  }

  return (
    <BaseTable
      sections={sections}
      columns={columns}
      getRowVariant={getRowVariant}
      emptyState="Nenhum OPME foi adicionado ainda"
    />
  )
}

export default OpmesTable
